import React from "react";
import { Box, Flex, Text, Button, Tag, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import DocumentWithLabelIcon from "@root/assets/icons/document";
import { useCertificateContext } from "@root/context/certificate-context";
import { useJournalistContext } from "@root/context/journalist-context";
import { useSubscriberContext } from "@root/context/use-subscriber-context";
import CustomSelect from "@root/components/common/CustomSelect";
import { ExcelIcon } from "@root/assets/icons/ExcelIcon";
import UserAvatar from "@root/components/course-drawer/UserAvatar";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import { formatProgress } from "@root/utils";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE } from "@root/constants";
import {
  DownloadButton,
  DrawerContainer,
  DrawerHeader,
  CourseTitleSection,
  CompletionBadge,
  DrawerClose,
  EnrolledCard,
  ProgressBackground,
  ProgressFill,
} from "@root/components/styles";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";

const statusTag = (status: number | null | undefined, t: any) => {
  if (status === null || status === undefined)
    return (
      <Tag size="sm" bg="#FFFAEB" color="orange" borderRadius="full" py={1}>
        ● {t("courseDrawer.notStarted")}
      </Tag>
    );
  if (status === 100)
    return (
      <Tag size="sm" bg="#ECFDF3" color="green" borderRadius="full" py={1}>
        ● {t("courseDrawer.completed")}
      </Tag>
    );
  if (status < 100)
    return (
      <Tag size="sm" bg="#FFFAEB" color="orange" borderRadius="full" py={1}>
        ● {t("courseDrawer.inProgress")}
      </Tag>
    );

  return null;
};

const noEnrolledJournalist = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="90"
      height="90"
      viewBox="0 0 90 90"
      fill="none"
    >
      <path
        d="M49.1666 86.6673H22.4619C16.0228 86.6673 10.9012 83.5341 6.30267 79.153C-3.11106 70.1845 12.3449 63.0173 18.2398 59.5073C28.2365 53.5548 40.177 51.907 51.2499 54.5639"
        stroke="#0073E0"
        stroke-width="6.25"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M63.75 22.084C63.75 32.4393 55.3553 40.834 45 40.834C34.6447 40.834 26.25 32.4393 26.25 22.084C26.25 11.7286 34.6447 3.33398 45 3.33398C55.3553 3.33398 63.75 11.7286 63.75 22.084Z"
        stroke="#0073E0"
        stroke-width="6.25"
      />
      <path
        d="M61.6665 86.666L74.1665 74.166M74.1665 74.166L86.6665 61.666M74.1665 74.166L61.6665 61.666M74.1665 74.166L86.6665 86.666"
        stroke="#0073E0"
        stroke-width="6.25"
        stroke-linecap="round"
      />
    </svg>
  );
};

interface CourseDrawerProps {
  open: boolean;
  onClose: () => void;
  course: any;
}

const CourseDrawer = ({ open, onClose, course }: CourseDrawerProps) => {
  const { t } = useTranslation("common");
  const user = useSelector(getLoggedInUser);
  // State declarations
  const [filteredCourseData, setFilteredCourseData] =
    React.useState<any>(course);
  const [selectedDepartment, setSelectedDepartment] = React.useState<any>(null);
  const [selectedRole, setSelectedRole] = React.useState<string>("");
  const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(
    null
  );
  const [enrollmentFilter, setEnrollmentFilter] =
    React.useState<string>("enrolled");
  const [pageNo, setPageNo] = React.useState<number>(1);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  // Tracks whether the drawer has received its first filtered fetch for the
  // current course. Used to show the spinner instead of an empty/"No Users
  // Found" state during the brief window between drawer mount and handleFilter.
  const [hasFetchedForCourse, setHasFetchedForCourse] =
    React.useState<boolean>(false);

  const pageSize = RECORDS_PER_PAGE;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Context hooks
  const {
    fetchMultiCertificates,
    fetchUserInCsv,
    fetchCertificate,
    isCourseLoading,
    isCsvLoading,
    isCertificateLoading,
    multiCertificatesLoading,
  } = useCertificateContext();
  const { fetchDepartments, departmentsData } = useJournalistContext();
  const { fetchSubscriberJournalists, fetchSubscriberJournalistLoading } =
    useSubscriberContext();

  // Enrollment status options
  const enrollmentOptions = React.useMemo(
    () => [
      { value: "enrolled", label: t("courseDrawer.enrolled") },
      { value: "unenrolled", label: t("courseDrawer.unenrolled") },
    ],
    [t]
  );

  // Update filtered data when course changes — clear rows immediately so the
  // user never sees stale members from a previous fetch while the new
  // course's filtered list is loading.
  React.useEffect(() => {
    setFilteredCourseData(course ? { ...course, rows: [] } : course);
  }, [course]);

  // Reset all filters, pagination, and state when course changes
  React.useEffect(() => {
    if (course?.courseId) {
      setSelectedDepartment(null);
      setSelectedRole("");
      setSelectedRoleId(null);
      setEnrollmentFilter("enrolled");
      setPageNo(1);
      setTotalCount(0);
      setHasFetchedForCourse(false);
    }
  }, [course?.courseId]);

  // Get filtered users from API response (no client-side filtering needed)
  const filteredUsers = filteredCourseData?.rows ?? [];

  // Check if at least one user has 100% progress
  const hasCompletedUser = course?.overallProgress > 0;

  const renderUserCard = (user: any, index: number) => {
    const isEnrolled = user?.courseProgress !== null;
    const progress = user?.courseProgress?.progress ?? 0;

    return (
      <Flex key={index} {...EnrolledCard}>
        <Flex gap={3}>
          <UserAvatar avatar={user?.avatar} fullName={user?.fullName} />

          <Box>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              {user?.fullName}
            </Text>
            {isEnrolled && (
              <Box w="200px">
                <Box {...ProgressBackground}>
                  <Box {...ProgressFill(progress)} />
                </Box>
              </Box>
            )}
          </Box>
        </Flex>

        <Box textAlign="right">
          {/* Download Certificate Button */}
          {isEnrolled && progress === 100 && (
            <Button
              fontSize="14px"
              px={2}
              py={0.5}
              color="#1819B4"
              borderRadius="full"
              fontWeight="500"
              display="inline-flex"
              alignItems="center"
              gap={1}
              w="fit-content"
              bg="transparent"
              h="auto"
              minH="auto"
              _hover={{
                bg: "#F3F4F6",
              }}
              onClick={() =>
                fetchCertificate({
                  courseId: course?.courseId,
                  subscriberId: user?.subscriberId,
                  userId: user?.userId,
                  gmtOffset: 0,
                  journalistId: user?.id,
                })
              }
              isLoading={isCertificateLoading(
                course?.courseId,
                user?.id,
                user?.userId
              )}
              disabled={isCertificateLoading(
                course?.courseId,
                user?.id,
                user?.userId
              )}
            >
              <DownloadIcon color="#1819B4" />
              {t("certificateSection.download")}
            </Button>
          )}

          {/* Enrollment Status Pill */}
          <Text
            as={Flex}
            alignItems="center"
            fontSize="xs"
            fontWeight="500"
            px={2}
            py={0.5}
            borderRadius="full"
            display="inline-flex"
            mb={2}
            bg={isEnrolled ? "#ECFDF3" : "#F2F4F7"}
            color={isEnrolled ? "#027A48" : "#667085"}
            mr={2}
          >
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={isEnrolled ? "#10B981" : "#9CA3AF"}
              mr={2}
            />
            {isEnrolled
              ? t("courseDrawer.enrolled")
              : t("courseDrawer.unenrolled")}
          </Text>

          {/* Progress Status Pill */}
          {isEnrolled && (
            <Text
              as={Flex}
              alignItems="center"
              fontSize="xs"
              fontWeight="500"
              px={2}
              py={0.5}
              borderRadius="full"
              display="inline-flex"
              mb={2}
              bg={
                progress === 100
                  ? "#D1FAE5"
                  : progress < 100
                  ? "#FEF3C7"
                  : "#F3F4F6"
              }
              color={
                progress === 100
                  ? "#065F46"
                  : progress < 100
                  ? "#92400E"
                  : "#6B7280"
              }
            >
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={
                  progress === 100
                    ? "#10B981"
                    : progress < 100
                    ? "#F97316"
                    : "#9CA3AF"
                }
                mr={2}
              />
              {progress < 100
                ? t("memberDetailsDrawer.inProgress")
                : progress === 100
                ? t("certificateSection.completed")
                : t("memberDetailsDrawer.notStarted")}
            </Text>
          )}

          {/* Progress Percentage */}
          {isEnrolled && (
            <Text fontSize="xs" mt={1} color="#2F2F34" fontWeight="500">
              {formatProgress(progress)}% {t("certificateSection.completed")}
            </Text>
          )}
        </Box>
      </Flex>
    );
  };

  // Function to handle filtering by department, role, and enrollment status with pagination
  const handleFilter = React.useCallback(async () => {
    if (!course?.courseId) return;

    // Build filter payload with pagination
    const filterPayload: any = {
      offset: (pageNo - 1) * pageSize,
      limit: pageSize,
    };

    // Add isEnrolled based on enrollment filter
    if (enrollmentFilter === "enrolled") {
      filterPayload.isEnrolled = "true";
    } else if (enrollmentFilter === "unenrolled") {
      filterPayload.isEnrolled = "false";
    } else {
      // "all" - send empty string
      filterPayload.isEnrolled = "";
    }

    // Only add departmentId if a department is selected
    if (selectedDepartment?.id) {
      filterPayload.departmentId = selectedDepartment.id;
    }

    // Only add roleId if a role is selected
    if (selectedRoleId) {
      filterPayload.roleId = selectedRoleId;
    }

    // Call fetchSubscriberJournalists with the courseId and filter payload
    const response = await fetchSubscriberJournalists(
      course.courseId,
      filterPayload
    );

    // Update the filtered course data with the response
    if (response) {
      setFilteredCourseData({
        ...filteredCourseData,
        rows: response.rows,
        // Preserve other properties from the original course
        courseId: course.courseId,
        maintitle: course.maintitle,
        overallProgress: course.overallProgress,
      });
      // Update total count for pagination
      setTotalCount(response.count || 0);
    }
    setHasFetchedForCourse(true);
  }, [
    course,
    selectedDepartment?.id,
    selectedRoleId,
    enrollmentFilter,
    pageNo,
    pageSize,
  ]);

  React.useEffect(() => {
    if (open) {
      const payload = {
        subscriberId: Number(user?.id),
      };
      fetchDepartments(payload);
    }
  }, [open]);

  // Reset page to 1 when filters change.
  React.useEffect(() => {
    setPageNo(1);
  }, [selectedDepartment?.id, selectedRoleId, enrollmentFilter]);

  // Immediately swap to the loader when filters or page change, so users
  // don't see stale results during the small gap before the API call's
  // loading flag flips on.
  React.useEffect(() => {
    setHasFetchedForCourse(false);
  }, [selectedDepartment?.id, selectedRoleId, enrollmentFilter, pageNo]);

  const handleDownloadCSV = () => {
    const payload = {
      courseId: course?.courseId,
    };
    fetchUserInCsv(payload);
  };

  // Apply filter when department, role, enrollment status, or page changes
  React.useEffect(() => {
    if (open && course?.courseId) {
      handleFilter();
    }
  }, [
    selectedDepartment?.id,
    selectedRoleId,
    enrollmentFilter,
    pageNo,
    course?.courseId,
    handleFilter,
    open,
  ]);
  return (
    <>
      {open && (
        <Flex {...DrawerContainer}>
          <Flex {...DrawerHeader}>
            <Flex {...CourseTitleSection}>
              <Text fontSize="xl" fontWeight="bold" color="#2F2F34">
                {course?.maintitle}
              </Text>
              <Flex {...CompletionBadge}>
                {formatProgress(course?.overallProgress)}%{" "}
                {t("courseDrawer.completed")}
              </Flex>
            </Flex>

            <Text {...DrawerClose} onClick={onClose}>
              ×
            </Text>
          </Flex>

          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            mb={6}
            gap={3}
            flexWrap="wrap"
            minHeight="44px"
          >
            <Flex
              gap={2}
              alignItems="center"
              flexWrap="wrap"
              flex="1"
              minWidth="0"
            >
              {/* Enrollment Status Filter */}
              <Box data-tour-item="pledge-course-drawer-enrollment-filter">
                <CustomSelect
                  options={enrollmentOptions}
                  value={enrollmentFilter === "all" ? "" : enrollmentFilter}
                  placeholder={t("courseDrawer.all")}
                  onChange={(value) => {
                    setEnrollmentFilter(value === "" ? "all" : value);
                  }}
                  width="160px"
                  height="44px"
                  isDisabled={false}
                />
              </Box>

              <Box data-tour-item="pledge-course-drawer-department-filter">
                <CustomSelect
                  options={
                    departmentsData?.rows?.map((dept: any) => ({
                      value: dept.title,
                      label: dept.title,
                    })) || []
                  }
                  value={selectedDepartment?.title || ""}
                  placeholder={t("courseDrawer.department")}
                  onChange={(value) => {
                    const dept = departmentsData?.rows?.find(
                      (d: any) => d.title === value
                    );
                    setSelectedDepartment(dept || null);
                    setSelectedRole("");
                    setSelectedRoleId(null);
                  }}
                  width="160px"
                  height="44px"
                  isDisabled={false}
                />
              </Box>

              <Box data-tour-item="pledge-course-drawer-role-filter">
                <CustomSelect
                  options={
                    selectedDepartment?.children?.map((role: any) => ({
                      value: role.title,
                      label: role.title,
                    })) || []
                  }
                  value={selectedRole || ""}
                  placeholder={t("courseDrawer.role")}
                  onChange={(value) => {
                    setSelectedRole(value);
                    const roleObj = selectedDepartment?.children?.find(
                      (r: any) => r.title === value
                    );
                    setSelectedRoleId(roleObj?.id || null);
                  }}
                  width="fit-content"
                  height="44px"
                  fontSize="14px"
                  isDisabled={!selectedDepartment}
                />
              </Box>
            </Flex>
            <Flex gap={2} flexShrink={0} flexWrap="wrap">
              <Box
                data-tour-item="pledge-course-drawer-download-csv"
                {...DownloadButton}
                onClick={handleDownloadCSV}
                height="44px"
                minWidth="fit-content"
                display="flex"
                alignItems="center"
                px={4}
                opacity={isCsvLoading(course?.courseId) ? 0.6 : 1}
                cursor={
                  isCsvLoading(course?.courseId) ? "not-allowed" : "pointer"
                }
                pointerEvents={isCsvLoading(course?.courseId) ? "none" : "auto"}
                flexShrink={0}
              >
                {isCsvLoading(course?.courseId) ? (
                  <Spinner size="sm" color="#1819B4" marginRight="10px" />
                ) : (
                  <ExcelIcon boxSize="4" marginRight="10px" color="#1819B4" />
                )}
                <Text whiteSpace="nowrap">
                  {t("subscriberManagement.downloadCSV")}
                </Text>
              </Box>
              <Button
                data-tour-item="pledge-course-drawer-export-certificates"
                border="1px solid #1819B4"
                borderRadius="8px"
                fontSize="14px"
                color={hasCompletedUser ? "#1819B4" : "#A7B2BA"}
                variant="outline"
                height="44px"
                fontWeight={500}
                minWidth="fit-content"
                px={4}
                display="flex"
                alignItems="center"
                _hover={{ bg: "#F3F4F6" }}
                isDisabled={!hasCompletedUser || multiCertificatesLoading}
                isLoading={multiCertificatesLoading}
                spinner={<Spinner size="sm" color="#1819B4" />}
                onClick={() =>
                  fetchMultiCertificates({
                    courseId: course?.courseId,
                    gmtOffset: 0,
                  })
                }
                flexShrink={0}
              >
                {!multiCertificatesLoading && (
                  <DocumentWithLabelIcon boxSize={20} />
                )}
                <Text
                  ml={2}
                  color={hasCompletedUser ? "#1819B4" : "#A7B2BA"}
                  whiteSpace="nowrap"
                >
                  {t("courseDrawer.export")}
                </Text>
              </Button>
            </Flex>
          </Flex>

          {(enrollmentFilter !== "all" ||
            !!selectedDepartment?.id ||
            !!selectedRoleId) && (
            <Flex
              alignItems="center"
              gap={2}
              mb={4}
              mt={-2}
              px={3}
              py={2}
              borderRadius="8px"
              bg="#EEF2FF"
              border="1px solid #C7CEFF"
            >
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg="#1819B4"
                flexShrink={0}
              />
              <Text fontSize="sm" color="#1819B4" fontWeight={500}>
                {enrollmentFilter === "enrolled"
                  ? t("courseDrawer.showingEnrolledHint")
                  : enrollmentFilter === "unenrolled"
                  ? t("courseDrawer.showingUnenrolledHint")
                  : t("courseDrawer.filtersAppliedHint")}
              </Text>
              <Box flex="1" />
              <Text
                fontSize="sm"
                color="#1819B4"
                fontWeight={500}
                cursor="pointer"
                textDecoration="underline"
                onClick={() => {
                  setEnrollmentFilter("all");
                  setSelectedDepartment(null);
                  setSelectedRole("");
                  setSelectedRoleId(null);
                }}
              >
                {t("courseDrawer.clearFilters")}
              </Text>
            </Flex>
          )}

          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontSize="md" fontWeight="semibold">
                {enrollmentFilter === "enrolled"
                  ? t("courseDrawer.enrolledUsers")
                  : enrollmentFilter === "unenrolled"
                  ? t("courseDrawer.noEnrolledUsers")
                  : t("courseDrawer.allUsers")}
              </Text>
              {totalCount > 0 && (
                <Text fontSize="sm" color="#667085">
                  {totalCount}{" "}
                  {totalCount === 1
                    ? t("courseDrawer.user")
                    : t("courseDrawer.users")}
                </Text>
              )}
            </Flex>

            {fetchSubscriberJournalistLoading || !hasFetchedForCourse ? (
              <Flex
                flexDir={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"500px"}
              >
                <Spinner size="xl" color="#1819B4" thickness="3px" />
              </Flex>
            ) : filteredUsers?.length === 0 ? (
              <Flex
                flexDir={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"500px"}
              >
                {noEnrolledJournalist()}
                <Text fontWeight={600} fontSize={"24px"} mt={7}>
                  {t("courseDrawer.noUsersFound")}
                </Text>
                <Text
                  fontWeight={400}
                  fontSize={"16px"}
                  mt={1}
                  color="#667085"
                  textAlign="center"
                >
                  {t("courseDrawer.noUsersFoundDescription")}
                </Text>
              </Flex>
            ) : (
              <>
                <Box>
                  {filteredUsers?.map((user: any, index: number) =>
                    renderUserCard(user, index)
                  )}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Flex mt={6} justifyContent="center">
                    <Paginate
                      page={pageNo}
                      count={totalCount}
                      pageSize={pageSize}
                      onPageChange={setPageNo}
                    />
                  </Flex>
                )}
              </>
            )}
          </Box>
        </Flex>
      )}
    </>
  );
};

export default CourseDrawer;
